import React, { useEffect, useState } from 'react'
import styled from 'styled-typed'
import { useFormikContext } from 'formik'
import { ArticleCrudData } from 'admin/articleService'
import { FormItem, FormItemName } from 'uiComponents/form/formElements'
import { SingleSelectFieldFormik, SingleSelectOption } from 'uiComponents/input'
import NumberInputFieldFormik from 'uiComponents/input/numberInput/numberInputFieldFormik'
import { FormTogglerCol, FormToggler } from 'uiComponents/input/toggle'
import { Body } from 'uiComponents/typography'
import Infotip from 'uiComponents/infotip'
import { Row, Col } from 'uiComponents/flex'
import { TicketDurationEditor } from './ticketDurationEditor'
import { t, useTranslation } from 'translations'

const MaxScansCol = styled(Col)`
    display: flex;
    align-items: center;
`

const getScanLimitTypeOptions = (): SingleSelectOption[] => [
    { name: t('products.crud.scanningOptions.limitType.purchaseDate'), value: 'scan_period_selected_date' },
    { name: t('products.crud.scanningOptions.limitType.validPeriod'), value: 'scan_period_fixed' },
    { name: t('products.crud.scanningOptions.limitType.firstScan'), value: 'scan_period_1st_scan' },
]

export function ScanningOptionsEditor() {
    const { t } = useTranslation()
    const {
        values: { scanningOptions },
        setFieldValue,
    } = useFormikContext<ArticleCrudData>()
    if (!scanningOptions) {
        return null
    }
    const [limitScans, setLimitScans] = useState<boolean>(scanningOptions.maxScans !== null)
    const [limitScansPerDay, setLimitScansPerDay] = useState<boolean>(scanningOptions.maxScansPerDay !== null)

    useEffect(() => {
        setLimitScans(scanningOptions.maxScans !== null)
        setLimitScansPerDay(scanningOptions.maxScansPerDay !== null)
    }, [scanningOptions])

    function onLimitScansChanged(value: boolean) {
        setLimitScans(value)
        if (!value) {
            setFieldValue('scanningOptions.maxScans', null)
        }
    }

    function onLimitScansPerDayChanged(value: boolean) {
        setLimitScansPerDay(value)
        if (!value) {
            setFieldValue('scanningOptions.maxScansPerDay', null)
        }
    }

    return (
        <Row>
            <Col span={6}>
                <Row>
                    <FormTogglerCol span={12}>
                        <FormItemName>{t('products.crud.scanningOptions.limitTotalScans')}</FormItemName>
                        <FormToggler isOn={limitScans} onClick={onLimitScansChanged} />
                    </FormTogglerCol>
                </Row>
                {limitScans && (
                    <Row>
                        <MaxScansCol span={12}>
                            <Body size={2}>{t('products.crud.scanningOptions.totalScansAllowed')}</Body>
                            <NumberInputFieldFormik
                                id="max-scans"
                                name="scanningOptions.maxScans"
                                integerOnly
                                min={0}
                                max={999}
                                placeholder="1"
                                style={{
                                    marginLeft: '.5rem',
                                }}
                                validationMessageProps={{
                                    marginLeft: '.5rem',
                                }}
                            />
                        </MaxScansCol>
                    </Row>
                )}
                <Row>
                    <FormTogglerCol span={12}>
                        <FormItemName>{t('products.crud.scanningOptions.limitScansPerDay')}</FormItemName>
                        <FormToggler isOn={limitScansPerDay} onClick={onLimitScansPerDayChanged} />
                    </FormTogglerCol>
                </Row>
                {limitScansPerDay && (
                    <Row>
                        <MaxScansCol span={12}>
                            <Body size={2}>{t('products.crud.scanningOptions.maxScansPerDay')}</Body>
                            <NumberInputFieldFormik
                                id="max-scans-per-day"
                                name="scanningOptions.maxScansPerDay"
                                integerOnly
                                min={1}
                                max={999}
                                placeholder="1"
                                style={{
                                    marginLeft: '.5rem',
                                }}
                                validationMessageProps={{
                                    marginLeft: '.5rem',
                                }}
                            />
                        </MaxScansCol>
                    </Row>
                )}
                <FormItem htmlFor="scan-limit-type">
                    <FormItemName>{t('products.crud.scanningOptions.scanLimitType')}</FormItemName>
                    <SingleSelectFieldFormik
                        id="scan-limit-type"
                        name="scanningOptions.scanLimitType"
                        options={getScanLimitTypeOptions()}
                        height="2.5rem"
                        style={{ marginBottom: '0.625em' }}
                    />
                </FormItem>
                <Col span={12}>
                    <FormItemName>
                        {t('products.crud.scanningOptions.scanningPeriodLength')}
                        <Infotip pointer="left" maxWidth="25em">
                            {t('products.crud.scanningOptions.scanningPeriodLengthTooltip')}
                        </Infotip>
                    </FormItemName>
                    <TicketDurationEditor name="scanningOptions.ticketDuration" />
                </Col>
            </Col>
        </Row>
    )
}
